import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  MdDashboard,
  MdArticle,
  MdWork,
  MdDescription,
  MdMail,
  MdBusinessCenter,
  MdPeople,
  MdImage,
  MdHelp,
  MdMenu,
  MdLogout,
  MdClose,
  MdStar,
  MdSecurity,
} from 'react-icons/md';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Check screen size and close sidebar on mobile
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const menuItems = [
    { path: '/admin/dashboard', icon: MdDashboard, label: 'Dashboard' },
    { path: '/admin/blogs', icon: MdArticle, label: 'Blogs' },
    { path: '/admin/careers', icon: MdWork, label: 'Careers/Jobs' },
    { path: '/admin/applications', icon: MdDescription, label: 'Applications' },
    { path: '/admin/contacts', icon: MdMail, label: 'Contact Messages' },
    { path: '/admin/services', icon: MdBusinessCenter, label: 'Services' },
    { path: '/admin/team', icon: MdPeople, label: 'Team' },
    { path: '/admin/gallery', icon: MdImage, label: 'Gallery' },
    { path: '/admin/faqs', icon: MdHelp, label: 'FAQs' },
    { path: '/admin/testimonials', icon: MdStar, label: 'Testimonials' },
    { path: '/admin/privacy-policy', icon: MdSecurity, label: 'Privacy Policy' },
  ];

  const activeLink = (path) =>
    location.pathname === path
      ? 'bg-[#008300] text-white'
      : 'hover:bg-[#008300]/10 hover:text-[#008300]';

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/admin/login';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-white shadow-xl z-50 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-0'
        } lg:w-64 lg:block`}
        style={{ display: sidebarOpen ? 'block' : 'none' }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-gray-200">
          <h1
            className={`font-bold text-[#008300] ${
              sidebarOpen ? 'text-xl' : 'text-2xl'
            }`}
          >
            {sidebarOpen ? 'Novarsis Admin' : 'N'}
          </h1>
        </div>

        {/* Menu */}
        <nav className="p-4 space-y-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeLink(
                item.path
              )}`}
            >
              <item.icon className="text-xl" />
              {sidebarOpen && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-4 left-0 right-0 px-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <MdLogout className="text-xl" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-300 ${
          sidebarOpen ? 'ml-64' : 'ml-0'
        } lg:ml-64`}
      >
        {/* Top Bar */}
        <header className="h-16 bg-white shadow-sm flex items-center justify-between px-6 sticky top-0 z-40">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {sidebarOpen ? (
              <MdClose className="text-2xl text-[#008300]" />
            ) : (
              <MdMenu className="text-2xl text-[#008300]" />
            )}
          </button>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Welcome, Admin
            </span>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
